import { getHexTileMapKey } from '../hexmap/model/keys';
import type { UnitUpgradeDef } from '../rules/schema';
import { milliCost, toMilli } from './fixed';
import { buildingLevel } from './levels';
import { deployUnit, occupancyOf, roomFor } from './occupancy';
import { canAfford, holdsStock, spend } from './stock';
import { capCount, capType, unitCap } from './systems/spawn';
import { mintOrder, territoryOwners } from './territory';
import type { EntityId, PlayerId, SimContext, SimState, Unit, UnitType } from './types';
import { unitMaxHp, unitRank } from './veterancy';

/**
 * Promotions: a unit type's `upgrades` (units.yaml) turn a unit into another
 * type in place (a worker into a labourer, an archer into a rifle squad),
 * for a price, at an own city meeting the upgrade's `at`. The unit keeps its
 * id, owner, xp (so its rank), group, home city and load; its hp keeps its
 * fraction of max.
 */

/** The upgrade taking `from` into `to`, if its rules list one. */
function upgradeOf(
  rules: SimContext['rules'],
  from: UnitType,
  to: UnitType
): UnitUpgradeDef | undefined {
  return rules.units[from]?.upgrades?.find((u) => u.to === to);
}

/**
 * What promoting `n` units of type `from` into `to` costs in all (the
 * upgrade's cost × n), for the UI to show before a click; undefined when
 * `from` has no upgrade into `to`.
 */
function promoteCost(
  rules: SimContext['rules'],
  from: UnitType,
  to: UnitType,
  n = 1
): Record<string, number> | undefined {
  const upgrade = upgradeOf(rules, from, to);
  if (!upgrade) return undefined;
  const total: Record<string, number> = {};
  for (const [res, amount] of Object.entries(upgrade.cost)) total[res] = amount * n;
  return total;
}

type PromoteRejection = 'unknown' | 'no-path' | 'where' | 'cost' | 'cap';

type PromoteCheck = { ok: true; upgrade: UnitUpgradeDef } | { ok: false; reason: PromoteRejection };

/**
 * Whether `unit` may be promoted into `to` now. Shared by the `promote`
 * command and the UI, so a disabled button and a refused click give the same
 * reason. Checked in order:
 *   - unknown: no such unit or type, or its owner is missing or out
 *   - no-path: its type has no upgrade into `to`
 *   - where: it doesn't stand in the territory of an own completed city,
 *     or that territory holds no completed own `at` building of that level
 *   - cost: its owner's cities together don't hold the upgrade's cost
 *   - cap: `to` counts against another cap than its type, and its owner
 *     already has as many units against that cap as it allows (a promotion
 *     within one cap, a worker into a labourer, never is, even over a cap
 *     shrunk by a fallen city)
 * `owners` (`territoryOwners`) may be passed in when checking many units.
 */
function canPromote(
  state: SimState,
  ctx: SimContext,
  unit: Unit | undefined,
  to: UnitType,
  knownOwners?: ReadonlyMap<string, EntityId>
): PromoteCheck {
  const { rules } = ctx;
  const player = unit && state.players[unit.owner];
  if (!unit || !player || player.eliminated || !rules.units[unit.type] || !rules.units[to]) {
    return { ok: false, reason: 'unknown' };
  }
  const upgrade = upgradeOf(rules, unit.type, to);
  if (!upgrade) return { ok: false, reason: 'no-path' };

  const owners = knownOwners ?? territoryOwners(state, rules);
  const cityId = owners.get(getHexTileMapKey(unit.pos));
  const city = cityId !== undefined ? state.buildings[cityId] : undefined;
  if (!city || city.owner !== unit.owner || !holdsStock(rules, city)) {
    return { ok: false, reason: 'where' };
  }
  const at = upgrade.at;
  if (
    at &&
    !Object.values(state.buildings).some(
      (b) =>
        b.type === at.building &&
        b.owner === unit.owner &&
        b.buildProgress >= 1 &&
        buildingLevel(b) >= at.level &&
        owners.get(getHexTileMapKey(b.pos)) === city.id
    )
  ) {
    return { ok: false, reason: 'where' };
  }

  if (!canAfford(state, unit.owner, milliCost(upgrade.cost))) return { ok: false, reason: 'cost' };
  // Within one cap (a worker into a labourer) the count doesn't change, so
  // only a promotion into another cap is checked (even over a shrunken cap).
  if (
    capType(rules, unit.type) !== capType(rules, to) &&
    capCount(state, rules, unit.owner, to) >= unitCap(state, rules, unit.owner, to)
  ) {
    return { ok: false, reason: 'cap' };
  }
  return { ok: true, upgrade };
}

/**
 * Promotes `unit` into `to` when `canPromote` allows it: pays the cost and
 * changes its type in place. Its hp keeps its fraction of max, its load is
 * clamped to the new `carryCapacity`, and its automation carries over (reset
 * when the new type automates differently, dropped when it doesn't at all).
 * Inside a building that doesn't take the new type (cavalry in a barracks
 * becoming a tank) it comes out onto a free tile around it; with none, the
 * promotion doesn't happen (nothing paid). Returns whether it happened.
 */
function promoteUnit(
  state: SimState,
  ctx: SimContext,
  unit: Unit,
  to: UnitType,
  owners?: ReadonlyMap<string, EntityId>
): boolean {
  const check = canPromote(state, ctx, unit, to, owners);
  if (!check.ok) return false;
  const { rules } = ctx;
  const from = unit.type;
  const fromDef = rules.units[from];
  const toDef = rules.units[to];
  if (!fromDef || !toDef) return false;

  const fraction = unit.hp / unitMaxHp(rules, unit);
  unit.type = to;
  const home = unit.inside ? state.buildings[unit.inside] : undefined;
  if (home && roomFor(rules, home, { id: unit.id, owner: unit.owner, type: to }) === 0) {
    if (!deployUnit(state, ctx, occupancyOf(state, rules), unit)) {
      unit.type = from;
      return false;
    }
  }
  spend(state, unit.owner, milliCost(check.upgrade.cost));
  unit.hp = Math.round(fraction * unitMaxHp(rules, unit));

  const capacity = toDef.carryCapacity === undefined ? undefined : toMilli(toDef.carryCapacity);
  if (unit.carry && capacity !== undefined && unit.carry.amount > capacity) {
    unit.carry.amount = capacity;
  }
  if (!toDef.automate) delete unit.auto;
  else if (unit.auto && toDef.automate !== fromDef.automate) unit.auto = {};
  return true;
}

/**
 * The `promote` command: each of the player's listed units (in the order
 * given, deduped) that may be promoted into `to` is, so a cap or the stock
 * running out stops the rest. Promotion moves no building, so the territory
 * is computed once for the whole command.
 */
function handlePromote(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  unitIds: EntityId[],
  to: UnitType
): void {
  const owners = territoryOwners(state, ctx.rules);
  for (const id of new Set(unitIds)) {
    const unit = state.units[id];
    if (unit?.owner === playerId) promoteUnit(state, ctx, unit, to, owners);
  }
}

/**
 * The units a city-wide `promote` picks from, best first: `playerId`'s units
 * of type `from` (any type with an upgrade into `to` when omitted) standing
 * in the territory of their completed city `cityId`, stored ones included;
 * the highest rank first, then the most hp, then the oldest. Not checked
 * against `canPromote` (the UI asks it why the first can't be promoted).
 * Empty when `cityId` isn't the player's completed city.
 */
function promoteCandidates(
  state: SimState,
  rules: SimContext['rules'],
  playerId: PlayerId,
  cityId: EntityId,
  to: UnitType,
  from?: UnitType,
  knownOwners?: ReadonlyMap<string, EntityId>
): Unit[] {
  const city = state.buildings[cityId];
  if (!city || city.owner !== playerId || !holdsStock(rules, city)) return [];
  const owners = knownOwners ?? territoryOwners(state, rules);
  return Object.values(state.units)
    .filter(
      (u) =>
        u.owner === playerId &&
        (from === undefined || u.type === from) &&
        u.type !== to &&
        !!upgradeOf(rules, u.type, to) &&
        owners.get(getHexTileMapKey(u.pos)) === cityId
    )
    .sort(
      (a, b) =>
        unitRank(rules, b) - unitRank(rules, a) || b.hp - a.hp || mintOrder(a.id) - mintOrder(b.id)
    );
}

/**
 * The city-wide `promote` command: promotes up to `count` (default 1;
 * 'all': every one it can) of `promoteCandidates`, best first, skipping any
 * `canPromote` refuses, so one click upgrades one unit without picking it.
 */
function handlePromoteInCity(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  cityId: EntityId,
  to: UnitType,
  from: UnitType | undefined,
  count: number | 'all' = 1
): void {
  const owners = territoryOwners(state, ctx.rules);
  const want = count === 'all' ? Number.POSITIVE_INFINITY : count;
  let promoted = 0;
  for (const unit of promoteCandidates(state, ctx.rules, playerId, cityId, to, from, owners)) {
    if (promoted >= want) break;
    if (promoteUnit(state, ctx, unit, to, owners)) promoted++;
  }
}

export type { PromoteCheck, PromoteRejection };
export {
  canPromote,
  handlePromote,
  handlePromoteInCity,
  promoteCandidates,
  promoteCost,
  upgradeOf,
};
